import * as React from "react";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  Button,
  useTheme,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { useNotification } from "../../provider/NotificationProvider";
import { useUser } from "../../provider/AuthProvider";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import stringToColor from "../../services/stringToColor";
import { Cancel, EmailRounded, LogoutSharp, Phone } from "@mui/icons-material";
import LangSwitcher from "../../components/languageswitcher";
import { useTranslation } from "react-i18next";

const Topbar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  const {
    unreadCount,
    markOneAsRead,
    notifications,
    fetchNotifications,
    markAllAsRead,
  } = useNotification();

  const [anchorNotif, setAnchorNotif] = useState(null);
  const openNotif = Boolean(anchorNotif);

  const handleNotifOpen = async (e) => {
    setAnchorNotif(e.currentTarget);
    await fetchNotifications(); // 👈 Fetch notifications on click
    //markAllAsRead(); // 👈 Reset badge
  };
  const handleNotifClose = () => setAnchorNotif(null);

  const [anchorProfile, setAnchorProfile] = useState(null);
  const openProfile = Boolean(anchorProfile);
  const handleProfileOpen = (e) => setAnchorProfile(e.currentTarget);
  const handleProfileClose = () => setAnchorProfile(null);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      ml={2}
      p="2px 8px"
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#0d1b2a" : "#f5f7fa",
        transition: "background-color 0.3s",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#22374D" : "#E3E3E3",
        },
        borderRadius: "12px",
        backdropFilter: "blur(20px)",
      }}
    >
      <Box></Box>

      <Box display="flex">
        <Tooltip title="Language">
          <LangSwitcher />
        </Tooltip>
        <Tooltip title="Toggle Light/Dark">
          <IconButton onClick={colorMode.toggleColorMode}>
            {theme.palette.mode === "dark" ? (
              <DarkModeOutlinedIcon fontSize="large" />
            ) : (
              <LightModeOutlinedIcon fontSize="large" />
            )}
          </IconButton>
        </Tooltip>

        <Tooltip title="Notifications">
          <IconButton onClick={handleNotifOpen}>
            <Badge
              badgeContent={unreadCount}
              color="error"
              // overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.7rem",
                  // height: 1,
                  // width: 10,
                },
              }}
            >
              <NotificationsOutlinedIcon fontSize="large" />
            </Badge>
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorNotif}
          open={openNotif}
          onClose={handleNotifClose}
          PaperProps={{
            sx: {
              minWidth: 330,
              borderRadius: 2,
              mt: 1.5,
              boxShadow: 6,
              maxHeight: 400,
            },
          }}
        >
          <Box px={2} py={1}>
            <Typography fontWeight="bold">Notifications</Typography>
          </Box>
          <Divider />
          {notifications.length === 0 ? (
            <MenuItem disabled>No new notifications</MenuItem>
          ) : (
            notifications.map((n) => (
              <MenuItem key={n.id} onClick={handleNotifClose}>
                <ListItem
                  disableGutters
                  disablePadding
                  sx={{ alignItems: "flex-start" }}
                  className="notranslate"
                >
                  <ListItemIcon className="notranslate">
                    {n.sender?.avatar_url ? (
                      <Avatar
                        alt={n.sender?.username}
                        src={n.sender?.avatar_url}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          bgcolor: stringToColor(n.sender?.FullName),
                        }}
                      >
                        {n.sender?.FullName.charAt(0).toUpperCase()}
                      </Avatar>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    className="notranslate"
                    primary={
                      <React.Fragment>
                        <Box>
                          <Typography
                            sx={{
                              marginLeft: "10px",
                            }}
                            variant="h5"
                          >
                            {n.title}
                          </Typography>
                          <Typography
                            sx={{
                              color: "text.primary",
                              display: "inline",
                              marginLeft: "10px",
                            }}
                            variant="h6"
                            className="notranslate"
                          >
                            ({n.sender?.username})
                          </Typography>
                        </Box>
                      </React.Fragment>
                    }
                    onClick={() => {
                      handleNotifClose();
                      navigate("/notification");
                    }}
                  ></ListItemText>
                </ListItem>
                <Box display="flex">
                  <Box>
                    <Typography>
                      {new Date(n.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    markOneAsRead(n.id);
                  }}
                >
                  <Cancel />
                </IconButton>
              </MenuItem>
            ))
          )}
        </Menu>

        <Tooltip title="Account">
          <IconButton onClick={handleProfileOpen} className="notranslate">
            {user?.avatar_url ? (
              <Avatar alt={user?.username} src={user?.avatar_url} />
            ) : (
              <Avatar
                sx={{
                  bgcolor: stringToColor(user?.FullName),
                }}
              >
                {user?.FullName.charAt(0).toUpperCase()}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorProfile}
          open={openProfile}
          onClose={handleProfileClose}
          PaperProps={{
            sx: {
              width: 300,
              borderRadius: 2,
              boxShadow: 4,
              mt: 1.5,
              overflow: "hidden",
            },
          }}
        >
          <Box px={2} py={1.5}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              className="notranslate"
            >
              {user?.avatar_url ? (
                <Avatar
                  className="notranslate"
                  alt={user?.username}
                  src={user?.avatar_url}
                  sx={{
                    width: 56,
                    height: 56,
                    boxShadow: 2,
                  }}
                />
              ) : (
                <Avatar
                  className="notranslate"
                  sx={{
                    bgcolor: stringToColor(user?.FullName),
                    width: 56,
                    height: 56,
                    boxShadow: 2,
                  }}
                >
                  {user?.FullName.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box className="notranslate">
                <Typography fontWeight="bold" fontSize="1rem">
                  {user?.username}
                </Typography>
                <Box display="flex">
                  <EmailRounded sx={{ mr: 2 }} color="info" />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {user?.email}
                  </Typography>
                </Box>
                <Box display="flex">
                  <Phone sx={{ mr: 2 }} color="success" />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    dir="ltr"
                    noWrap
                  >
                    {user?.phone}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="center">
            <Button onClick={logout} startIcon={<LogoutSharp />}>
              Logout
            </Button>
          </Box>
        </Menu>
      </Box>
    </Box>
  );
};

export default Topbar;
